<script setup lang="ts">
import { useAuthentication, useUserContext } from '@ilteducation/ilt-auth';
import { computed, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute, useRouter } from 'vue-router';
import { useLocale } from '@/plugins/translation';
import { useMyOrganizations } from '@ilteducation/my-account';
import config from '../config/ConfigProvider';

const serviceName = config.value('serviceName');
const logoImageFile = config.value('logoImageFile');

const { t, l } = useLocale();

const { createLoginLink, authenticateWithCode } = useAuthentication();
const { reset: resetMyOrganizations } = useMyOrganizations(false, 10);

const basePath = config.value('basePath');
const redirectBasePath = `${basePath}login`;

const clientId = config.value('authClientId');
const redirectToLogin = (reauthenticate?: string) => {
  window.location.href = createLoginLink({
    appName: serviceName,
    clientId,
    reauthenticate,
    redirectBasePath,
  });
};

const router = useRouter();
const route = useRoute();

const errorMessage = ref<string | undefined>();
const showPage = ref(false);

setTimeout(() => {
  showPage.value = true;
}, 1000);

const afterLogin = () => {
  const savedRoute = JSON.parse(localStorage.getItem('savedRouteOnLogin')!);
  if (savedRoute) {
    localStorage.removeItem('savedRouteOnLogin');
    router.push(savedRoute);
  } else {
    router.push({ name: 'begreppaLanding' });
  }
};

const userContext = useUserContext();
const { isLoggedIn, isInitialised } = storeToRefs(userContext);

const checkAuthentication = () => {
  if (isLoggedIn.value) {
    afterLogin();
  } else if (route.query.code) {
    resetMyOrganizations();
    authenticateWithCode(serviceName, route.query.code as string, clientId, redirectBasePath)
      .then(() => afterLogin())
      .catch(() => {
        errorMessage.value = t(l.login.login_error) as string;
      });
  } else if (isInitialised.value) {
    // reset my organization state, so we don't end up with miss match user and organizations.
    resetMyOrganizations();
    redirectToLogin('auto');
  }
};

watch(isInitialised, () => {
  checkAuthentication();
});

checkAuthentication();

const logoImage = computed(() => new URL(`/src/assets/images/${logoImageFile}`, import.meta.url).href);
</script>

<template>
  <v-container v-if="showPage" fluid class="text-center">
    <v-row justify="center">
      <v-col xs="12" md="5" lg="3">
        <v-img :src="logoImage" max-height="60px" alt="logo" />
      </v-col>
    </v-row>
    <v-row class="mb-2" justify="center">
      <v-col class="text-secondary">
        <h3>{{ t(`login.message_welcome_to_service_${serviceName}`) }}</h3>
      </v-col>
    </v-row>
    <v-row justify="center">
      <v-col class="text-secondary">
        <p>{{ t(l.login.message_service_description) }}</p>
      </v-col>
    </v-row>
    <v-row>
      <v-col class="d-flex flex-column align-center">
        <span id="loading-label">{{ t(l.login.login_processing) }}</span>
        <v-progress-circular
          class="mt-4"
          indeterminate
          size="50"
          color="primary"
          aria-labelledby="loading-label"
        ></v-progress-circular>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-alert v-if="errorMessage" border="start" color="red-lighten-2">
          {{ errorMessage }}
        </v-alert>
      </v-col>
    </v-row>
  </v-container>
</template>

<style scoped></style>
